import React, { useState, useEffect, useRef, useCallback } from 'react';
import { spanish } from '../../api';
import { playSpanish } from '../../utils/spanishAudio';
import './Spanish.css';

/**
 * Coach / headcoach: record (or upload) the audio students hear for each Spanish word.
 * Words without a recording fall back to the phone's built-in Spanish voice.
 */
export default function SpanishAudioManager({ onClose }) {
  const [units, setUnits] = useState([]);
  const [courses, setCourses] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [recId, setRecId] = useState(null);     // item currently recording
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('all');  // all | missing
  const recorder = useRef(null);
  const chunks = useRef([]);
  const fileInput = useRef(null);
  const uploadFor = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await spanish.items();
      setUnits(res.data.units);
      setCourses(res.data.courses || []);
      setItems(res.data.items);
    } catch (e) {
      setError('Could not load the word list.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (item, blob, name) => {
    setBusyId(item._id);
    setError('');
    try {
      const res = await spanish.uploadAudio(item._id, blob, name);
      setItems(list => list.map(i => i._id === item._id ? { ...i, audioUrl: res.data.audioUrl, audioAt: new Date().toISOString() } : i));
    } catch (e) {
      setError(e.response?.data?.error || 'Upload failed.');
    } finally {
      setBusyId(null);
    }
  };

  const startRecording = async (item) => {
    setError('');
    if (!navigator.mediaDevices || !window.MediaRecorder) { setError('Recording is not supported here - use "Upload" instead.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const type = rec.mimeType || 'audio/webm';
        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        upload(item, new Blob(chunks.current, { type }), `${item.key}.${ext}`);
      };
      recorder.current = rec;
      rec.start();
      setRecId(item._id);
    } catch (e) {
      setError('Microphone access was blocked. Allow it in your browser/phone settings, or use "Upload".');
    }
  };

  const stopRecording = () => {
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    setRecId(null);
  };

  const remove = async (item) => {
    setBusyId(item._id);
    try {
      await spanish.deleteAudio(item._id);
      setItems(list => list.map(i => i._id === item._id ? { ...i, audioUrl: null } : i));
    } catch (e) {
      setError('Could not remove the recording.');
    } finally {
      setBusyId(null);
    }
  };

  // audioUrl = your own recording; defaultAudioUrl = the standard Spanish voice students hear otherwise
  const withAudio = items.filter(i => i.audioUrl).length;
  const shown = filter === 'missing' ? items.filter(i => !i.audioUrl) : items;

  return (
    <div className="sp-overlay" onClick={onClose}>
      <div className="sp-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sp-sheet-top">
          <h3 style={{ margin: 0, flex: 1 }}>🎙️ Spanish audio</h3>
          <button className="sp-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="sp-sub">{withAudio} of {items.length} words have your own recording. The rest use the standard Spanish voice (female, Spain).</p>
        <div className="sp-seg" style={{ marginBottom: 8 }}>
          <button className={filter === 'all' ? 'on-ok' : ''} onClick={() => setFilter('all')}>All</button>
          <button className={filter === 'missing' ? 'on-ok' : ''} onClick={() => setFilter('missing')}>Missing ({items.length - withAudio})</button>
        </div>
        {error && <div className="sp-error">{error}</div>}

        <input ref={fileInput} type="file" accept="audio/*" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f && uploadFor.current) upload(uploadFor.current, f, f.name); }} />

        {units.map((u, idx) => {
          const rows = shown.filter(i => i.unit === u.key);
          if (rows.length === 0) return null;
          const courseTitle = (courses.find(c => c.n === u.course) || {}).title;
          const firstOfCourse = !units.slice(0, idx).some(x => x.course === u.course && shown.some(i => i.unit === x.key));
          return (
            <div key={u.key} style={{ marginTop: 10 }}>
              {firstOfCourse && courseTitle && <div className="sp-course-head" style={{ marginTop: 14 }}><h3>{courseTitle}</h3></div>}
              <div className="sp-unit-title">{u.icon} {u.title}</div>
              {rows.map(it => (
                <div className="sp-row" key={it._id}>
                  <div className="sp-phrase-main">
                    <div className="sp-es" style={{ fontSize: 15 }}>{it.es}</div>
                    <div className="sp-en">{it.en}</div>
                  </div>
                  <button className="sp-play" onClick={() => playSpanish({ ...it, audioUrl: it.audioUrl || it.defaultAudioUrl })} aria-label={`Play ${it.es}`}>{it.audioUrl ? '🎙️' : '🔊'}</button>
                  <div className="sp-row-actions">
                    {recId === it._id ? (
                      <button className="sp-btn sp-btn--sm sp-btn--warn" onClick={stopRecording}><span className="sp-rec" />Stop</button>
                    ) : (
                      <button className="sp-btn sp-btn--sm" disabled={busyId === it._id || !!recId} onClick={() => startRecording(it)}>
                        {busyId === it._id ? '…' : it.audioUrl ? 'Re-record' : '🎙️ Record yours'}
                      </button>
                    )}
                    <button className="sp-btn sp-btn--sm sp-btn--ghost" disabled={busyId === it._id || !!recId}
                      onClick={() => { uploadFor.current = it; fileInput.current.click(); }}>Upload</button>
                    {it.audioUrl && <button className="sp-btn sp-btn--sm sp-btn--ghost" disabled={busyId === it._id} onClick={() => remove(it)} aria-label="Remove recording">🗑</button>}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        <p className="sp-sub" style={{ marginTop: 12 }}>🎙️ = your recording · 🔊 = standard voice. Removing your recording (🗑) brings the standard voice back. Standard voice: Piper es_ES-sharvard (CC BY 3.0, Sharvard corpus).</p>
      </div>
    </div>
  );
}
